import React from 'react';
import { MobxComponent } from "../../../mobx/components";
import { inject, observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { IconClose, IconBack } from "./icons";
import { Switch, SelectServices } from "@lib/components";
import { MenuItem, QuickSettingsContainer, MenuColorHeaderWrapper, MenuColorHeaderTitleWrapper, MenuColorDescription, MenuColorBodyWrapper, MenuColorFormGroup, MenuColorLabel, MenuColorFooterWrapper, MenuToggleContainer, MenuToggleText, MenuLineSeparator, MenuPublishPlatformContainer, MenuPublishPlatFormItem, MenuPublishPlatFormItemHeader, MenuPublishPlatFormItemHeaderLabel, ButtonV2 } from "./layout.c";
import { ListServicesOptionsNoB } from '@lib/common';
import { cloneDeep } from 'lodash';

/** Mirrors menu_v2 / category_v2: "defines" pos/online when those objects carry at least one key. */
export function menuDefinesPosOnlineBlocks(conditions: any): {
    defines: boolean;
    pos: any;
    online: any;
} {
    const sourcePos = conditions?.pos;
    const sourceOnline = conditions?.online;
    const defines =
        (sourcePos != null && typeof sourcePos === 'object' && Object.keys(sourcePos).length > 0) ||
        (sourceOnline != null && typeof sourceOnline === 'object' && Object.keys(sourceOnline).length > 0);
    return { defines, pos: sourcePos, online: sourceOnline };
}

/**
 * Merges parent pos/online into a resolved availability row only where the child’s `pos` or `online`
 * is still `undefined` or `null`. If both are already set (including empty objects), returns null.
 */
export function mergeAvailabilityWithPosOnline(
    resolved: any,
    sourcePos: any,
    sourceOnline: any
): any | null {
    const base = resolved && typeof resolved === 'object' ? cloneDeep(resolved) : {};
    let changed = false;

    const parentHasPos =
        sourcePos != null && typeof sourcePos === 'object' && Object.keys(sourcePos).length > 0;
    const parentHasOnline =
        sourceOnline != null &&
        typeof sourceOnline === 'object' &&
        Object.keys(sourceOnline).length > 0;

    if (parentHasPos && (resolved == null || resolved.pos === undefined || resolved.pos === null)) {
        base.pos = cloneDeep(sourcePos);
        changed = true;
    }
    if (
        parentHasOnline &&
        (resolved == null || resolved.online === undefined || resolved.online === null)
    ) {
        base.online = cloneDeep(sourceOnline);
        changed = true;
    }

    return changed ? base : null;
}

/** Aligns MenuVisibility payload with form save semantics for `menuDefinesPosOnlineBlocks`. */
export function normalizeMenuVisibilityConditionsForSave(
    state: MenuVisibilityConditions
): MenuVisibilityConditions {
    const pos = state.pos;
    const online = state.online;
    const posDefines =
        !!(pos?.enabled || (pos?.services && pos.services.length > 0));
    const onlineDefines =
        !!(online?.enabled || (online?.services && online.services.length > 0));
    return {
        pos: posDefines ? { enabled: !!pos?.enabled, services: pos?.services ?? [] } : ({} as any),
        online: onlineDefines
            ? { enabled: !!online?.enabled, services: online?.services ?? [] }
            : ({} as any),
        external_platforms:
            state.external_platforms ?? { uber: { enabled: false }, abacus: { enabled: false } },
    };
}

export interface MenuVisibilityConditions {
    pos?: { enabled: boolean; services: string[] };
    online?: { enabled: boolean; services: string[] };
    external_platforms?: { uber?: { enabled: boolean }; abacus?: { enabled: boolean } };
}

/** Resolved category availability row id (category row or menu default). */
export function effectiveCategoryAvailabilityId(menu: any, category: any): string | undefined {
    if (!menu) return undefined;
    if (!category) return menu.availability_id;
    const cid = category.availability_id;
    if (cid == null || cid === '') return menu.availability_id;
    return cid;
}

/** Resolved item availability row id (item, category, or menu). */
export function effectiveItemAvailabilityId(menu: any, category: any, item: any): string | undefined {
    if (!menu || !item) return undefined;
    const iid = item.availability_id;
    if (iid != null && iid !== '') return iid;
    return effectiveCategoryAvailabilityId(menu, category);
}

/** Category still uses the menu’s shared availability slot. */
export function categoryInheritsMenuAvailability(menu: any, category: any): boolean {
    if (!menu || !category) return true;
    const cid = category.availability_id;
    if (cid == null || cid === '') return true;
    return cid === menu.availability_id;
}

/** Item still inherits its category (or menu) availability slot. */
export function itemInheritsParentAvailability(menu: any, category: any, item: any): boolean {
    if (!item) return true;
    const iid = item.availability_id;
    if (iid == null || iid === '') return true;
    return iid === effectiveCategoryAvailabilityId(menu, category);
}

/**
 * Clone a base availability row and apply normalized POS/online (+ platforms) with a new row id.
 * Used when forking off the parent slot so item/category edits do not mutate the shared menu row.
 */
export function buildForkedAvailabilityRowWithVisibility(
    baseSlot: any,
    conditions: MenuVisibilityConditions,
    newSlotId: string
): any {
    const norm = normalizeMenuVisibilityConditionsForSave(conditions);
    const row = cloneDeep(baseSlot && typeof baseSlot === 'object' ? baseSlot : {});
    row.id = newSlotId;
    row.pos = norm.pos;
    row.online = norm.online;
    row.external_platforms = norm.external_platforms;
    return row;
}

interface Props extends WithTranslation {
    show?: boolean;
    onHide?: () => void;
    onSave?: (conditions: MenuVisibilityConditions) => void;
    title?: string;
    initialConditions?: MenuVisibilityConditions;
}

interface State extends MenuVisibilityConditions {}

@inject("store") @observer
class MenuVisibilityClass extends MobxComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        const c = props.initialConditions;
        this.state = {
            pos: c?.pos ?? { enabled: false, services: [] },
            online: c?.online ?? { enabled: false, services: [] },
            external_platforms: c?.external_platforms ?? { uber: { enabled: false }, abacus: { enabled: false } }
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.initialConditions !== prevProps.initialConditions) {
            const c = this.props.initialConditions;
            this.setState({
                pos: c?.pos ?? { enabled: false, services: [] },
                online: c?.online ?? { enabled: false, services: [] },
                external_platforms: c?.external_platforms ?? { uber: { enabled: false }, abacus: { enabled: false } }
            });
        }
    }


    render() {
        const { show, onHide, onSave, title } = this.props;
        const { pos, online, external_platforms } = this.state;

        return (
            <QuickSettingsContainer className={show ? 'active' : ''}>
                <MenuColorHeaderWrapper>
                    <MenuItem onClick={onHide}><IconBack /></MenuItem>
                    <MenuColorHeaderTitleWrapper>
                        {title || ''} {title ? '- ' : ''} Visibility
                    </MenuColorHeaderTitleWrapper>
                    <MenuItem onClick={onHide}><IconClose /></MenuItem>
                </MenuColorHeaderWrapper>
                <MenuColorBodyWrapper style={{ minHeight: '50vh', overflowY: 'scroll' }}>
                    <MenuColorLabel>This item will be active on</MenuColorLabel>
                    <MenuColorFormGroup>
                        <MenuColorLabel>POS</MenuColorLabel>
                        <MenuToggleContainer>
                            <MenuToggleText>{pos?.enabled ? 'On' : 'Off'}</MenuToggleText>
                            <Switch
                                id="pos-active"
                                checked={!!pos?.enabled}
                                onChange={(e) => this.setState({ pos: { ...pos!, enabled: e.target.checked, services: pos?.services ?? [] } })}
                            />
                        </MenuToggleContainer>
                    </MenuColorFormGroup>
                    <MenuColorLabel>Select Service Type</MenuColorLabel>
                    <SelectServices
                        type="multi"
                        value={pos?.services ?? []}
                        onChange={(options: string[]) => this.setState({ pos: { ...pos!, services: options } })}
                        options={ListServicesOptionsNoB}
                    />
                    <MenuLineSeparator />
                    <MenuColorFormGroup>
                        <MenuColorLabel>Online</MenuColorLabel>
                        <MenuToggleContainer>
                            <MenuToggleText>{online?.enabled ? 'On' : 'Off'}</MenuToggleText>
                            <Switch
                                id="online-active"
                                checked={!!online?.enabled}
                                onChange={(e) => this.setState({ online: { ...online!, enabled: e.target.checked, services: online?.services ?? [] } })}
                            />
                        </MenuToggleContainer>
                    </MenuColorFormGroup>
                    <MenuColorLabel>Select Service Type</MenuColorLabel>
                    <SelectServices
                        type="multi"
                        value={online?.services ?? []}
                        onChange={(options: string[]) => this.setState({ online: { ...online!, services: options } })}
                        options={ListServicesOptionsNoB}
                    />
                    <MenuLineSeparator />
                    {/*<MenuColorLabel>Publish on other platforms</MenuColorLabel>*/}
                    {/*<MenuPublishPlatformContainer className='quick-sidebar' style={{width: '100%'}}>*/}
                    {/*    <MenuPublishPlatFormItem>*/}
                    {/*        <MenuPublishPlatFormItemHeader>*/}
                    {/*            <MenuPublishPlatFormItemHeaderLabel>Uber</MenuPublishPlatFormItemHeaderLabel>*/}
                    {/*            <MenuToggleContainer>*/}
                    {/*                <MenuToggleText>{external_platforms?.uber?.enabled ? 'On' : 'Off'}</MenuToggleText>*/}
                    {/*                <Switch*/}
                    {/*                    id="uber-enabled"*/}
                    {/*                    checked={!!external_platforms?.uber?.enabled}*/}
                    {/*                    onChange={(e) => this.setState({*/}
                    {/*                        external_platforms: {*/}
                    {/*                            ...external_platforms,*/}
                    {/*                            uber: { enabled: e.target.checked }*/}
                    {/*                        }*/}
                    {/*                    })}*/}
                    {/*                />*/}
                    {/*            </MenuToggleContainer>*/}
                    {/*        </MenuPublishPlatFormItemHeader>*/}
                    {/*        /!*<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et.</p>*!/*/}
                    {/*    </MenuPublishPlatFormItem>*/}
                    {/*    <MenuPublishPlatFormItem>*/}
                    {/*        <MenuPublishPlatFormItemHeader>*/}
                    {/*            <MenuPublishPlatFormItemHeaderLabel>Abacus</MenuPublishPlatFormItemHeaderLabel>*/}
                    {/*            <MenuToggleContainer>*/}
                    {/*                <MenuToggleText>{external_platforms?.abacus?.enabled ? 'On' : 'Off'}</MenuToggleText>*/}
                    {/*                <Switch*/}
                    {/*                    id="abacus-enabled"*/}
                    {/*                    checked={!!external_platforms?.abacus?.enabled}*/}
                    {/*                    onChange={(e) => this.setState({*/}
                    {/*                        external_platforms: {*/}
                    {/*                            ...external_platforms,*/}
                    {/*                            abacus: { enabled: e.target.checked }*/}
                    {/*                        }*/}
                    {/*                    })}*/}
                    {/*                />*/}
                    {/*            </MenuToggleContainer>*/}
                    {/*        </MenuPublishPlatFormItemHeader>*/}
                    {/*        /!*<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et.</p>*!/*/}

                    {/*    </MenuPublishPlatFormItem>*/}
                    {/*</MenuPublishPlatformContainer>*/}
                </MenuColorBodyWrapper>
                <MenuColorFooterWrapper>
                    <ButtonV2 variant="default" onClick={onHide}>Cancel</ButtonV2>
                    <ButtonV2
                        onClick={() =>
                            onSave?.(normalizeMenuVisibilityConditionsForSave(this.state))
                        }
                    >
                        Save
                    </ButtonV2>
                </MenuColorFooterWrapper>
            </QuickSettingsContainer>
        );
    }
}
export const MenuVisibility = withTranslation()(MenuVisibilityClass);
